// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import { DetailInvoice } from '~/components/module/invoice';
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseSplitButton, Loading, UnderDevelopment } from '~/components/common';
// Others
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { EMPTY_STRING } from '~/utils/constants/common';
import { InvoiceSendModeEnum, RoleEnum, StatusEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import {
  IChangeStatusInvoiceBody,
  IInvoiceDetail,
  IQueryGetInvoiceDetails,
  ISendInvoiceBody,
} from '~/utils/interface/invoice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { changeStatusInvoice, getInvoiceDetails, sendInvoice } from '~/thunks/invoice/invoiceThunk';
import { invoiceActions, selectIsRefreshInvoiceDetail } from '~/thunks/invoice/invoiceSlice';
// Styles, images, icons
import styles from './InvoiceDetail.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const InvoiceDetail = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice', 'common/base']);
  const navigate = useNavigate();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const dispatch = useAppDispatch();
  const { id: invoiceId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  const isRefreshInvoiceDetail = useSelector(selectIsRefreshInvoiceDetail);
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoiceDetails, setInvoiceDetails] = useState<IInvoiceDetail>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetInvoiceDetails = useCallback(
    (params: IQueryGetInvoiceDetails) => {
      setIsLoading(true);

      dispatch(getInvoiceDetails(params))
        .unwrap()
        .then((res) => {
          if (!res || !res?.data) return;

          const data: IInvoiceDetail = res?.data;
          setInvoiceDetails(data);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!invoiceId) return;

    handleGetInvoiceDetails({ invoiceId: String(invoiceId) });
  }, [invoiceId, handleGetInvoiceDetails]);

  useEffect(() => {
    if (!isRefreshInvoiceDetail || !invoiceId) return;

    handleGetInvoiceDetails({ invoiceId: String(invoiceId) });
    dispatch(invoiceActions.setRefreshInvoiceDetail(false));
  }, [isRefreshInvoiceDetail, invoiceId, handleGetInvoiceDetails, dispatch]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBackToInvoice = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.invoice);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.invoice);
        break;
    }
  };

  const handleSendInvoice = () => {
    if (!invoiceId) return;

    const params: ISendInvoiceBody = {
      invoiceId: invoiceId,
      body: { sendMode: InvoiceSendModeEnum.SEND },
    };
    setIsLoading(true);

    dispatch(sendInvoice(params))
      .unwrap()
      .then((res) => {})
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePayInvoice = () => {
    if (!invoiceId) return;
    setIsLoading(true);

    const payload: IChangeStatusInvoiceBody = {
      invoiceId: invoiceId,
      body: { status: StatusEnum.PAID },
    };

    dispatch(changeStatusInvoice(payload))
      .unwrap()
      .then((res) => {
        dispatch(invoiceActions.setRefreshInvoiceDetail(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='invoiceDetailPage'
      title={t('page_detail_invoice_title')}
      onBackToPreviousPage={handleBackToInvoice}
      childrenToolbar={
        <Box component={'div'} className={cx('button')}>
          <BaseSplitButton
            label={t('common_action_send', { ns: 'common/base' })}
            onClick={handleSendInvoice}
            isCheckToggle={invoiceDetails?.status !== StatusEnum.PAID}
          >
            <Button variant='text' fullWidth onClick={handlePayInvoice}>
              {t('common_action_pay', { ns: 'common/base' })}
            </Button>
          </BaseSplitButton>
        </Box>
      }
    >
      <Box component={'div'} className={cx('container')}>
        <Box component={'div'} className={cx('viewContent')}>
          <DetailInvoice data={invoiceDetails} />
        </Box>
        <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
        {isLoading && <Loading />}
      </Box>
    </ChildrenPage>
  );
};

export default InvoiceDetail;
